import { t } from 'i18next';
import { cloneElement, isValidElement, useEffect, useState, createRef, useRef, useMemo } from 'react';
import uniqueId from '../../utils/uniqueId';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faSort, faSortUp, faSortDown, faTimesCircle, faSearch } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Select from 'react-select';

import Button from './Button';

import './Grid.scss';

const mapHeadersToFilters = (h, d, p) => {
    let newFilterObjects = {},
        i = 0,
        limit = h?.length ? h.length : 0;
    for (i; i < limit; i++) {
        if (h[i].filterOn && h[i].colName !== p) {
            let newFilterObject = {};
            let colName = h[i].colName;
            newFilterObject[h[i].colLabel] = d.rows.map((ele) => {
                return ele[colName];
            });
            newFilterObject[h[i].colLabel] = {
                options: Array.from(new Set(newFilterObject[h[i].colLabel])),
                dataId: colName,
            };
            newFilterObjects = Object.assign(newFilterObjects, newFilterObject);
        }
    }

    return newFilterObjects;
};

const mapPrimaryFilterOptions = (d, p) => {
    let newFilterOptions = new Set(),
        i = 0,
        limit = d?.rows?.length ? d.rows.length : 0;
    for (i; i < limit; i++) {
        newFilterOptions.add(d.rows[i][p]);
    }

    newFilterOptions = Array.from(newFilterOptions);

    newFilterOptions = newFilterOptions.map((filter) => {
        return {
            value: filter.replace(' ', '').toLowerCase(),
            label: filter,
        };
    });

    return newFilterOptions;
};

export default function Grid(props) {
    const [myId] = useState(uniqueId());
    const [initialized, setInitialized] = useState(false);
    const [displayCounter] = useState(typeof props.counter === 'boolean' ? props.counter : true);
    const [title] = useState(props?.title ? props.title : '');
    const [selectableRow] = useState(typeof props?.selectable === 'boolean' ? props.selectable : true);
    const [hoverableRow] = useState(typeof props?.hoverable === 'boolean' ? props.hoverable : true);
    const [selectedRow, setSelectedRow] = useState(-1);
    const [hoveredRow, setHoveredRow] = useState(-1);
    const [searchable] = useState(typeof props?.search === 'boolean' ? props.search : false);
    const [searchTerm, setSearchTerm] = useState('');
    const searchRef = useRef();
    const [sort, setSort] = useState(props?.initialSort ? props.initialSort : null);
    const [sortDirection, setSortDirection] = useState(
        typeof props?.initialSortDirection === 'boolean' ? props.initialSortDirection : null
    );
    const [headers] = useState(props?.headers ? props.headers : null);
    const [gridTemplateColumns] = useState(() => {
        let columnSizes = [];
        headers.forEach((header) => {
            columnSizes.push(typeof header?.colSize === 'string' ? header.colSize : 'auto');
        });

        return columnSizes;
    });
    const [data, setData] = useState(props?.data ? props.data : null);
    const [pageSize, setPageSize] = useState(props?.pageSize ? props.pageSize : data?.rows?.length);
    const [currentPage, setCurrentPage] = useState(pageSize ? 0 : null);
    const [maxPages, setMaxPages] = useState(
        !isNaN(pageSize) && !isNaN(data?.rows?.length) ? Math.ceil(data.rows.length / pageSize) : null
    );
    const [filters] = useState(typeof props?.filters === 'boolean' ? props.filters : true);
    const [currentFilters, setCurrentFilters] = useState(props?.filters && props.filters === true ? [] : null);
    const [addFiltersOpen, setAddFiltersOpen] = useState(false);
    const [selectedFilterOption, setSelectedFilterOption] = useState();
    const [primaryFilter] = useState(!!props?.primaryFilter ? props.primaryFilter : null);
    const [filterObjects] = useState(mapHeadersToFilters(headers, data, primaryFilter));
    const [filtersAdded, setFiltersAdded] = useState(0);
    const [primaryFilterOptions] = useState(!!primaryFilter ? mapPrimaryFilterOptions(data, primaryFilter) : null);
    const [primaryFiltersAdded, setPrimaryFiltersAdded] = useState([]);
    const [gridName, setGridName] = useState(!!props?.name && typeof props?.name === 'string' ? props.name : 'nameless-grid');

    const filterRefs = [];
    for (let i = 0; i < Object.keys(filterObjects).length; i++) {
        filterRefs.push(createRef());
    }

    const titleJSX =
        typeof title === 'string' && title !== '' ? (
            <div
                id={'grid-title-' + myId}
                className="grid-title"
            >
                {title}
            </div>
        ) : isValidElement(title) ? (
            title
        ) : null;

    const columnsJSX = data?.rows?.every((row) => row?.columns?.every((column) => isValidElement(column)))
        ? data.rows.flatMap((row, rowIndex) => {
              let rowClass = rowIndex === selectedRow ? 'selected-row' : '';
              let rowClass2 = rowIndex === hoveredRow ? 'hovered-row' : '';
              return row.columns.map((column, columnIndex) => {
                  let cellClass = (rowIndex % pageSize) % 2 !== 0 ? 'grid-cell' : 'odd grid-cell';
                  cellClass += ' ' + rowClass + ' ' + rowClass2;
                  cellClass += columnIndex === 0 ? ' begin' : columnIndex === row.columns.length - 1 ? ' end' : '';
                  let cellClick = () => {
                      if (selectableRow) setSelectedRow(rowIndex);
                      if (typeof column?.props?.onClick === 'function') {
                          column.props.onClick();
                      }
                  };
                  let cellHover = () => {
                      if (hoverableRow) setHoveredRow(rowIndex);
                  };
                  let cellNoHover = () => {
                      if (hoverableRow) setHoveredRow(-1);
                  };
                  return cloneElement(column, {
                      className: column?.props?.className ? column.props.className + ' ' + cellClass : cellClass,
                      key: 'grid-cell_' + rowIndex + '_' + columnIndex,
                      onClick: cellClick,
                      onMouseEnter: cellHover,
                      onMouseLeave: cellNoHover,
                  });
              });
          })
        : null;

    const primaryFilterJSX = useMemo(() => {
        return !!primaryFilter ? (
            <div className="primary-filter-container">
                <label>{primaryFilter}:</label>
                <br />
                <Select
                    className="primary-filter-dropdown"
                    classNamePrefix="primary-filter-dropdown"
                    value={primaryFiltersAdded.map((filter) => {
                        return {
                            value: filter.replace(' ', '').toLowerCase(),
                            label: filter,
                        };
                    })}
                    options={primaryFilterOptions}
                    onChange={(event) => {
                        setPrimaryFiltersAdded(event.map((i) => i.label));
                    }}
                    placeholder="All applications"
                    isSearchable={false}
                    isMulti
                />
            </div>
        ) : null;
    }, [primaryFilter, primaryFiltersAdded, primaryFilterOptions]);

    useEffect(() => {
        if (typeof props?.initialSort === 'string' && typeof props?.initialSortDirection === 'boolean' && initialized === false) {
            setInitialized(true);
            let sortFunc = headers.find(
                (element) => typeof element.sortFunc === 'function' && element.colName === props.initialSort
            )?.sortFunc;

            let newData =
                typeof sortFunc === 'function'
                    ? Object.assign(
                          {},
                          {
                              rows: data?.rows.sort(sortFunc(props.initialSortDirection)),
                          }
                      )
                    : {};

            setData(newData);
        }
    }, [props.initialSort, props.initialSortDirection, data, headers, initialized]);

    useEffect(() => {
        let newMaxPageSize = Math.ceil(data.rows.length / pageSize);
        if (newMaxPageSize !== maxPages) {
            setMaxPages(newMaxPageSize);
        }
    }, [data.rows.length, pageSize, maxPages]);

    useEffect(() => {
        let newData = Object.assign({}, props.data);
        if (primaryFiltersAdded.length > 0) {
            newData.rows = newData.rows.filter((row) => primaryFiltersAdded.includes(row[primaryFilter]));
        }

        if (!!sort) {
            let sortFunc = headers.find((element) => typeof element.sortFunc === 'function' && element.colName === sort)?.sortFunc;

            newData.rows = newData.rows.sort(sortFunc(sortDirection));
        }

        if (currentFilters) {
            let filterKeys = new Set();
            let _filters = [];
            currentFilters.forEach((filter) => filterKeys.add(filter.id));
            filterKeys = Array.from(filterKeys);
            filterKeys.forEach((key) => {
                let _newFilterLogic = {
                    filterId: key,
                    filterValues: [],
                };
                currentFilters.forEach((filter) => {
                    if (filter.id === key) {
                        _newFilterLogic.filterValues.push(filter.filterValue);
                    }
                });
                _filters.push(_newFilterLogic);
            });

            newData.rows = newData.rows.filter((row) => {
                let included = true;
                _filters?.forEach((filter) => {
                    if (!filter.filterValues.includes(row[filter.filterId])) {
                        included = false;
                    }
                });
                return included;
            });
        }

        if (searchable) {
            if (searchTerm.includes(',')) {
                let _searchTerm = searchTerm.split(',');
                _searchTerm = _searchTerm.map((term) => term.trim());

                newData.rows = newData.rows.filter((row) => {
                    let _row = Object.values(row);
                    let hasSearchTerm = [];
                    _row.forEach((data) => {
                        _searchTerm.forEach((term) => {
                            if (
                                typeof data?.toUpperCase === 'function' &&
                                typeof data?.toUpperCase()?.includes === 'function' &&
                                data.includes(term)
                            ) {
                                hasSearchTerm.push(true);
                            } else if (typeof data?.toString === 'function' && data.toString().toUpperCase().includes(term)) {
                                hasSearchTerm.push(true);
                            } else if (typeof data?.toString === 'function' && data.toString().includes(new Date(term))) {
                                hasSearchTerm.push(true);
                            }
                        });
                    });
                    return hasSearchTerm.length === _searchTerm.length;
                });
            } else {
                newData.rows = newData.rows.filter((row) => {
                    let _row = Object.values(row);
                    let hasSearchTerm = false;
                    _row.forEach((data) => {
                        if (!data) return;
                        if (
                            typeof data.toUpperCase === 'function' &&
                            typeof data.toUpperCase().includes === 'function' &&
                            data.includes(searchTerm)
                        ) {
                            hasSearchTerm = true;
                        } else if (data.toString().toUpperCase().includes(searchTerm)) {
                            hasSearchTerm = true;
                        } else if (data.toString().includes(new Date(searchTerm))) {
                            hasSearchTerm = true;
                        }
                    });
                    return hasSearchTerm;
                });
            }
        }

        if (JSON.stringify(newData) !== JSON.stringify(data)) {
            setData(newData);
            setCurrentPage(0);
        }
        if (!props.pageSize) setPageSize(newData.rows.length);
    }, [
        currentFilters,
        props.data,
        props.pageSize,
        searchable,
        searchTerm,
        data,
        primaryFilter,
        primaryFiltersAdded,
        primaryFiltersAdded.length,
        sort,
        sortDirection,
        headers,
    ]);

    useEffect(() => {
        setGridName((prev) => (!!props?.name && typeof props?.name === 'string' ? props.name : prev));
    }, [props.name]);

    useEffect(() => {
        let _filters = JSON.parse(window.sessionStorage.getItem(gridName));

        if (!!_filters) {
            setCurrentFilters(_filters.secondaryFilters);
            setPrimaryFiltersAdded(_filters.primaryFilters);
            setSearchTerm(_filters.search);
            setCurrentPage(parseInt(_filters.page));
        }
    }, [gridName]);

    useEffect(() => {
        let _newObject = {
            primaryFilters: primaryFiltersAdded,
            secondaryFilters: currentFilters,
            search: searchTerm,
            page: currentPage,
        };

        window.sessionStorage.setItem(gridName, JSON.stringify(_newObject));
    }, [currentFilters, primaryFiltersAdded, searchTerm, currentPage, gridName]);

    const nextPage = () => {
        if (currentPage + 1 < maxPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage - 1 >= 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const headerClick = (event) => {
        let newSortDirection = sortDirection !== null && event?.target?.attributes?.colname?.value === sort ? !sortDirection : false;
        let newSort = event?.target?.attributes?.colname?.value;
        let sortFunc = headers.find((element) => typeof element.sortFunc === 'function' && element.colName === newSort)?.sortFunc;

        if (sortFunc) {
            let newData =
                typeof sortFunc === 'function'
                    ? Object.assign(
                          {},
                          {
                              rows: data?.rows.sort(sortFunc(newSortDirection)),
                          }
                      )
                    : {};
            setSortDirection(newSortDirection);
            setSort(newSort);
            setData(newData);
            setCurrentPage(0);
        }
    };

    const columnFrom = currentPage * headers.length * pageSize;
    const columnTo = pageSize * headers.length;

    const determineHeaderClass = (header) => {
        let classes = [];
        if (sort === header?.colName) {
            if (sortDirection) {
                classes.push('sorting-by-desc');
            } else {
                classes.push('sorting-by-asc');
            }
        }

        if (typeof header?.sortFunc === 'function') {
            classes.push('sortable');
        }

        classes.push('grid-header');
        return classes.join(' ');
    };

    const addFilterClick = () => {
        subdropdownCancel();
        setAddFiltersOpen(!addFiltersOpen);
    };

    const filterOptionClick = (filterName) => {
        setSelectedFilterOption(filterName);
    };

    const subdropdownCancel = () => {
        setSelectedFilterOption();
    };

    const addFilterToGrid = (name, i, id) => {
        let newCurrentFilters = [];
        let newFilter = {
            id: id,
            filterValue: filterRefs[i].current.value,
            label: name + ': ' + filterRefs[i].current.value,
        };
        if (currentFilters !== null) {
            newCurrentFilters = newCurrentFilters.concat(currentFilters);
        }

        newCurrentFilters = newCurrentFilters.concat([newFilter]);
        let lengthBefore = newCurrentFilters.length;

        //Prevent duplicates
        newCurrentFilters = newCurrentFilters.filter((value, index) => {
            const _value = JSON.stringify(value);
            return (
                index ===
                newCurrentFilters.findIndex((obj) => {
                    return JSON.stringify(obj) === _value;
                })
            );
        });

        let lengthAfter = newCurrentFilters.length;

        if (lengthBefore > lengthAfter) {
            toast.info(t('Grid.FilterAlreadyAdded'));
        } else {
            newCurrentFilters.sort((a, b) => {
                return a.id < b.id ? -1 : 1;
            });

            setCurrentFilters(newCurrentFilters);
            setCurrentPage(0);
            setFiltersAdded(filtersAdded + 1);
            addFilterClick();
        }
    };

    const removeFilter = (filterId, filterValue) => {
        let newCurrentFilters = [];
        if (currentFilters !== null) {
            newCurrentFilters = newCurrentFilters.concat(currentFilters);
        }
        let filterIndex = newCurrentFilters.findIndex((obj) => {
            return obj.id === filterId && obj.filterValue === filterValue;
        });
        newCurrentFilters.splice(filterIndex, 1);
        setCurrentFilters(newCurrentFilters);
    };

    const moveSelectedRow = (event) => {
        if (
            selectableRow &&
            event?.key === 'ArrowDown' &&
            selectedRow + 1 <= headers.length - 1 &&
            pageSize &&
            selectedRow + 1 <= pageSize - 1
        ) {
            setSelectedRow(selectedRow + 1);
        } else if (selectableRow && event?.key === 'ArrowUp' && selectedRow - 1 > -1) {
            setSelectedRow(selectedRow - 1);
        }
    };

    const searchJSX = useMemo(() => {
        const search = () => {
            let _searchTerm = searchRef?.current?.value?.toUpperCase();

            if (typeof _searchTerm === 'undefined') return;
            else {
                setSearchTerm(_searchTerm);
            }
        };

        return (
            <>
                <FontAwesomeIcon
                    icon={faSearch}
                    className="grid-search-icon"
                    onClick={search}
                />

                <input
                    type="text"
                    placeholder="Search"
                    defaultValue={searchTerm}
                    ref={searchRef}
                    onKeyDown={(event) => {
                        if (event?.key === 'Enter') search();
                    }}
                    onChange={(event) => {
                        if (event.target.value === '') {
                            search();
                        }
                    }}
                />
            </>
        );
    }, [searchTerm]);

    return (
        <div className={props?.className ? props.className + ' grid-component-container' : 'grid-component-container'}>
            {primaryFilterJSX}
            <div
                className={props?.className ? props.className + ' grid-container' : 'grid-container'}
                onKeyUp={moveSelectedRow}
                tabIndex={0}
            >
                {titleJSX}
                {filters ? (
                    <div className="upper">
                        <div className="filter-container">
                            <Button
                                type="secondary"
                                onClick={addFilterClick}
                                className="add-filter"
                            >
                                + Add filters
                            </Button>
                            {addFiltersOpen ? (
                                <div className="filter-dropdown-container">
                                    <ul className="filters-dropdown">
                                        {Object.entries(filterObjects).map((filter, i) => {
                                            return (
                                                <li key={'list-item-key-' + filter[0] + '-' + i}>
                                                    <div
                                                        onClick={() => {
                                                            filterOptionClick(filter[0]);
                                                        }}
                                                    >
                                                        {filter[0]}
                                                    </div>
                                                    <div
                                                        className={
                                                            'filters-sub-dropdown' + (filter[0] !== selectedFilterOption ? ' hidden' : '')
                                                        }
                                                    >
                                                        <div className="sub-dropdown-title">{filter[0]}</div>
                                                        <select ref={filterRefs[i]}>
                                                            {filter[1].options.map((filterOption) => {
                                                                return (
                                                                    <option
                                                                        key={filter[0] + '-option-' + filterOption}
                                                                        value={filterOption}
                                                                    >
                                                                        {filterOption}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>

                                                        <div className="filter-button-container">
                                                            <Button
                                                                type="grey"
                                                                onClick={subdropdownCancel}
                                                            >
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                type="tertiary"
                                                                onClick={() => {
                                                                    addFilterToGrid(filter[0], i, filter[1].dataId);
                                                                }}
                                                            >
                                                                Add
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ) : null}
                        </div>
                        {searchable ? <div className="grid-search-container with-filter">{searchJSX}</div> : null}
                    </div>
                ) : (
                    <div className="upper">
                        {searchable ? <div className="grid-search-container">{searchJSX}</div> : null}
                        {displayCounter ? <div className="applications-counter no-filter">{data.rows.length + ' applications'}</div> : null}
                    </div>
                )}
                {currentFilters ? (
                    <div className="filters-and-applications-container">
                        <div className="current-filters-container">
                            {currentFilters.map((currentFilter) => {
                                return (
                                    <div
                                        className="current-filter"
                                        key={'current-filter-' + currentFilter.id + '-' + currentFilter.filterValue}
                                    >
                                        {currentFilter.label}&nbsp;
                                        <FontAwesomeIcon
                                            icon={faTimesCircle}
                                            onClick={() => {
                                                removeFilter(currentFilter.id, currentFilter.filterValue);
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="applications-counter">{data.rows.length + ' applications'}</div>
                    </div>
                ) : null}
                <div
                    className={'grid grid-' + myId}
                    style={{ gridTemplateColumns: gridTemplateColumns.join(' ') }}
                    aria-labelledby={title !== '' ? 'grid-title-' + myId : null}
                >
                    {headers?.map((header, i) => {
                        return (
                            <div
                                colname={header?.colName ? header.colName : null}
                                className={determineHeaderClass(header)}
                                onClick={headerClick}
                                key={'header-' + i}
                            >
                                {header?.colLabel ? header.colLabel : null}&nbsp;
                                <FontAwesomeIcon
                                    icon={faSort}
                                    className="dual-sort-icon"
                                />
                                <FontAwesomeIcon
                                    icon={faSortUp}
                                    className="up-sort-icon"
                                />
                                <FontAwesomeIcon
                                    icon={faSortDown}
                                    className="down-sort-icon"
                                />
                            </div>
                        );
                    })}
                    <div className="break grid-break" />
                    {pageSize ? columnsJSX?.slice(columnFrom, columnFrom + columnTo) : columnsJSX}
                </div>
                {props?.pageSize && maxPages > 1 ? (
                    <div className="page-buttons-container">
                        <button
                            className="prev-page-button"
                            onClick={prevPage}
                            disabled={currentPage === 0}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <span className="current-page-display">{currentPage + 1}</span>
                        <span className="current-of-max">{t('General.XofY')}</span>
                        <span className="max-page-display">{maxPages}</span>
                        <button
                            className="next-page-button"
                            onClick={nextPage}
                            disabled={currentPage === maxPages - 1}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
